import { UseSuspenseQueryResult } from "@tanstack/react-query";
import client, { StatusError, throwCaughtError } from "../client";
import { IntercomEvent, Visit } from "@/types";
import { trackEvent } from "@intercom/messenger-js-sdk";
import { REAL_ESTATE } from "@/lib";
import { useBuildingSuspenseQuery } from "@/components/RouterSetup";

const PersonalVisitorsQueryFn = async (userId: string) => {
  try {
    const response = await client.get(`/getPersonVisitors?userId=${userId}`);
    return response.data;
  } catch (error) {
    throwCaughtError(error);
  }
};

export const personalVisitorsOptions = (userId: string) => ({
  queryKey: ["personal-visitors", userId],
  queryFn: () => PersonalVisitorsQueryFn(userId),
});

export const usePersonalVisitors = (
  userId: string | undefined = "",
): UseSuspenseQueryResult<Visit[]> => {
  return useBuildingSuspenseQuery(personalVisitorsOptions(userId));
};

const CompanyVisitorsQueryFn = async (buildingTenantId: string) => {
  try {
    if (!buildingTenantId) throw new Error("buildingTenantId is required");
    const response = await client.get(
      `/getCompanyVisitors?buildingTenantId=${buildingTenantId}`,
    );
    return response.data;
  } catch (error) {
    throwCaughtError(error);
  }
};

export const companyVisitorsOptions = (buildingTenantId: string) => ({
  queryKey: ["company-visitors", buildingTenantId],
  queryFn: () => CompanyVisitorsQueryFn(buildingTenantId),
});

export const useCompanyVisitors = (
  buildingTenantId: string,
): UseSuspenseQueryResult<Visit[]> => {
  return useBuildingSuspenseQuery(companyVisitorsOptions(buildingTenantId));
};

export const buildingVisitorsOptions = (refetchInterval = 2500) => ({
  queryKey: ["visitors-building"],
  refetchInterval,
  queryFn: async () => {
    try {
      const response = await client.get("/getVisitorsBuilding");
      return response.data.visits;
    } catch (error) {
      throwCaughtError(error);
    }
  },
});

export const useBuildingVisitors = (): UseSuspenseQueryResult<Visit[]> => {
  return useBuildingSuspenseQuery(buildingVisitorsOptions());
};

export type LetInFailure = {
  code?: string;
  retryAllowed?: boolean;
};

export const getLetInFailure = (error: unknown): LetInFailure => {
  const data = (error as StatusError)?.data;
  return {
    code: data?.code,
    retryAllowed:
      typeof data?.retryAllowed === "boolean" ? data.retryAllowed : undefined,
  };
};

export const getLetInFailureMessageKey = ({
  code,
  retryAllowed,
}: LetInFailure): string => {
  switch (code) {
    case "SMS_SEND_FAILED":
      return "ANSWER_VISITOR.SMS_SEND_FAILED";
    case "ACCESS_CODE_GENERATION_FAILED":
      return retryAllowed
        ? "ANSWER_VISITOR.CODE_GENERATION_FAILED"
        : "ANSWER_VISITOR.ERROR_SENDING_MESSAGE";
    case "ACCESS_CODE_RETRY_NOT_ALLOWED":
      return "ANSWER_VISITOR.ERROR_SENDING_MESSAGE";
    case "LET_IN_RESULT_NOT_SAVED":
      return "ANSWER_VISITOR.LET_IN_RESULT_NOT_SAVED";
    case "ALREADY_LET_IN":
      return "ANSWER_VISITOR.ALREADY_LET_IN";
    case "ALREADY_CHECKED_IN":
      return "ANSWER_VISITOR.ALREADY_CHECKED_IN";
    case "LET_IN_IN_PROGRESS":
      return "ANSWER_VISITOR.LET_IN_IN_PROGRESS";
    default:
      return "ANSWER_VISITOR.LET_IN_ERROR";
  }
};

export const letInVisitor = async (
  userId: string,
  visitorId: string,
  company: boolean,
  name: string,
  phone: string,
  language: string,
  buildingTenantId: string,
  hostId: string,
) => {
  try {
    const response = await client.post("/letInVisitor", {
      userId,
      visitorId,
      company,
      name,
      phone,
      language,
      buildingTenantId,
      hostId,
    });
    trackEvent(IntercomEvent.VISITOR_ACCESS_GRANTED, {
      building_id: REAL_ESTATE,
    });
    return response.data;
  } catch (error) {
    throwCaughtError(error);
  }
};

export const checkInPreregisteredAttendee = async (
  attendeeName: string,
  attendeePhone: string,
  eventId: string,
  buildingTenantId: string,
  hostEmail: string,
  hostId: string,
  checkedInById: string,
  checkedInByName: string,
) => {
  try {
    const response = await client.post("/checkInPreregistrationAttendee", {
      attendeeName,
      attendeePhone,
      eventId,
      buildingTenantId,
      hostEmail,
      hostId,
      checkedInById,
      checkedInByName,
    });
    return response.data;
  } catch (error) {
    throwCaughtError(error);
  }
};

export const updateVisitorsMessageHistory = async (
  userId: string,
  visitorId: string,
  senderId: string,
  senderName: string,
  body: string,
  company: boolean = false,
  type?: string,
) => {
  try {
    const response = await client.patch("/updateVisitorMessageHistory", {
      userId,
      visitorId,
      message: { senderId, senderName, body, type },
      company,
    });
    return response.data;
  } catch (error) {
    throwCaughtError(error);
  }
};
